import { useEffect } from "react";
import { ArrayStore, DataStore, Feedback, Timeout, getRandomKey } from "shelving";
import { useStore } from "shelving/react";

// Constants.
const NOTICE_AUTO_HIDE = 3000; // How long before notices hide themselves.
const NOTICE_CLOSE_DELAY = 1000; // How long before hidden notices are removed from the DOM (allow time for animates to complete).

// Types.
export type NoticeStatus = "loading" | "success" | "error" | "info";
export type NoticeContent = string | React.ReactElement | Feedback;

/** Store a list of notices. */
export class NoticesStore extends ArrayStore<NoticeStore> {
	/** Add a normal notice to this list of notices. */
	notice(content: NoticeContent): NoticeStore {
		return new NoticeStore(this, "info", content);
	}

	/** Add a "success" notice to this list of notices. */
	success(content: NoticeContent): NoticeStore {
		return new NoticeStore(this, "success", content);
	}

	/** Add an "error" notice to this list of notices. */
	error(content: NoticeContent): NoticeStore {
		return new NoticeStore(this, "error", content);
	}

	/** Add a "loading" notice to this list of notices. */
	load(content?: NoticeContent): NoticeStore {
		return new NoticeStore(this, "loading", content);
	}
}

type NoticeData = {
	readonly isHidden: boolean;
	readonly status: NoticeStatus;
	readonly content: NoticeContent | null;
};

/** Store a single notice. */
export class NoticeStore extends DataStore<NoticeData> {
	private _notices: NoticesStore;

	readonly id = getRandomKey();

	constructor(notices: NoticesStore, status: NoticeStatus = "info", content: NoticeContent | null = null) {
		super({ isHidden: true, status, content });
		this._notices = notices;
		notices.add(this);
	}

	/** Show this notice. */
	readonly show = (): void => {
		this.setProp("isHidden", false);
	};

	/** Hide this notice. */
	readonly hide = (): void => {
		this.setProp("isHidden", true);
	};

	/** Close this notice (permanently). */
	readonly close = (): void => {
		{
			this.setProp("isHidden", true);
			setTimeout(() => this._notices.delete(this), NOTICE_CLOSE_DELAY);
		}
	};

	/** Change the status of this notice to none and set new content. */
	notice(content: NoticeContent) {
		this.update({ status: "info", content });
	}

	/** Change the status of this notice to "success" and set new content. */
	success(content: NoticeContent) {
		this.update({ status: "success", content });
	}

	/** Change the status of this notice to "fail" and set new content. */
	error(content: NoticeContent) {
		this.update({ status: "error", content });
	}

	/** Change the status of this notice to "loading" and set new content. */
	load(content: NoticeContent | null = null) {
		this.update({ status: "loading", content });
	}

	// Override to automatically close this notice a few seconds after it's opened (after the user has had chance to read it).
	public override get value(): NoticeData {
		return super.value;
	}
	public override set value(data: NoticeData) {
		super.value = data;
		const { isHidden, status } = data;
		if (!isHidden && status !== "loading") this._timeout.set();
		else this._timeout.clear();
	}
	private _timeout = new Timeout(this.close, NOTICE_AUTO_HIDE);
}

/** List that holds the currently active list of notice items. */
export const NOTICES = new NoticesStore();

/** Show the notices in a `NoticesStore` */
export function Notices({ list = NOTICES }: { list?: NoticesStore }): React.ReactElement {
	useStore(list);
	return (
		<div className="notices">
			{list.value.map(notice => (
				<NoticesNotice key={notice.id} notice={notice} />
			))}
		</div>
	);
}

/** Individual notice in a `<Notices>` */
function NoticesNotice({ notice }: { notice: NoticeStore }): React.ReactElement {
	// Rerender this component when the `NoticeStore` changes.
	useStore(notice);
	const { value, show } = notice;
	const { isHidden, content, status } = value;

	// Show the notice after it's successfully mounted.
	// Toggles the `.hidden` class so the opening animate starts.
	useEffect(() => void requestAnimationFrame(show), [show]);

	return (
		<aside className={`notice notice-${status} ${status === "loading" ? "effect-waiting" : ""}`} role={status === "success" || status === "error" ? "alert" : "status"} hidden={isHidden} aria-label={status}>
			{content instanceof Feedback ? content.message : content}
		</aside>
	);
}
